import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { hasBerlinOffset, isRfc3339WithOffset, rfc3339ToDate } from "@workspace/datetime";
import { bucketAcceptedTaskTypeKey, buckets, isTaskBucketTitle, taskBucketTitle, } from "../drizzle/buckets.js";
import { department, isAllowedChannel, isAllowedDepartment, taskChannel } from "../drizzle/taskTypes.js";
import { bucketTypeSchema } from "./bucketTypes.js";
import { taskSchema } from "./tasks.js";
import { userSchema } from "./users.js";
export const acceptedTaskTypeTitle = {
    [bucketAcceptedTaskTypeKey.DAMAGE_APPRAISAL]: taskBucketTitle.INBOX,
    [bucketAcceptedTaskTypeKey.INCIDENT_REPORT]: taskBucketTitle.INBOX,
    [bucketAcceptedTaskTypeKey.INBOX_PROCESSING]: taskBucketTitle.INBOX,
    [bucketAcceptedTaskTypeKey.COST_ESTIMATE]: taskBucketTitle.COST_ESTIMATE,
    [bucketAcceptedTaskTypeKey.INVOICE]: taskBucketTitle.INVOICE,
};
function isBucketAcceptedTaskType(value) {
    return Object.hasOwn(acceptedTaskTypeTitle, value);
}
export function titleAcceptingTaskType(acceptedTaskType) {
    return isBucketAcceptedTaskType(acceptedTaskType) ? acceptedTaskTypeTitle[acceptedTaskType] : undefined;
}
/**
 * A Task Bucket title needs an accepted task type from its own list, every other title needs none and
 * no channel or department either. The channel and the department are null (any) or allowed for the task
 * type. An inbox-processing bucket must name a department, because its mail fetch targets exactly one
 * eClaim inbox. Null when the bucket is valid.
 */
export function planningKeyMismatch(data) {
    const title = data.title != null && isTaskBucketTitle(data.title) ? data.title : null;
    if (title == null) {
        const field = ["acceptedTaskType", "acceptedChannel", "acceptedDepartment"].find((key) => data[key] != null);
        return field ? { reason: "not_a_task_bucket", field } : null;
    }
    const { acceptedTaskType, acceptedChannel, acceptedDepartment } = data;
    if (acceptedTaskType == null)
        return { reason: "missing", title };
    if (!isBucketAcceptedTaskType(acceptedTaskType) || acceptedTaskTypeTitle[acceptedTaskType] !== title) {
        return { reason: "not_accepted", title, acceptedTaskType };
    }
    if (acceptedChannel != null && !isAllowedChannel(acceptedTaskType, acceptedChannel)) {
        return { reason: "channel_not_allowed", acceptedTaskType, acceptedChannel };
    }
    if (acceptedDepartment != null && !isAllowedDepartment(acceptedTaskType, acceptedDepartment)) {
        return { reason: "department_not_allowed", acceptedTaskType, acceptedDepartment };
    }
    if (acceptedTaskType === bucketAcceptedTaskTypeKey.INBOX_PROCESSING && acceptedDepartment == null) {
        return { reason: "department_missing", acceptedTaskType };
    }
    return null;
}
function isTouched(stored, next) {
    return next !== undefined && next !== (stored ?? null);
}
export function planningKeyTouched(stored, next) {
    return (isTouched(stored.title, next.title) ||
        isTouched(stored.acceptedTaskType, next.acceptedTaskType) ||
        isTouched(stored.acceptedChannel, next.acceptedChannel) ||
        isTouched(stored.acceptedDepartment, next.acceptedDepartment));
}
function overlay(stored, next) {
    return next !== undefined ? next : stored;
}
/**
 * A stored bucket that breaks the rules is accepted unchanged unless the request touches its title,
 * task type, channel or department. Touching any of them checks the stored values overlaid with the
 * request; nothing is cleared implicitly. Null when the update is valid.
 */
export function planningKeyMismatchOnUpdate(stored, next) {
    if (!planningKeyTouched(stored, next))
        return null;
    return planningKeyMismatch({
        title: next.title ?? stored.title,
        acceptedTaskType: overlay(stored.acceptedTaskType, next.acceptedTaskType),
        acceptedChannel: overlay(stored.acceptedChannel, next.acceptedChannel),
        acceptedDepartment: overlay(stored.acceptedDepartment, next.acceptedDepartment),
    });
}
export function validatePlanningKeyOnUpdate(stored, next) {
    const mismatch = planningKeyMismatchOnUpdate(stored, next);
    return mismatch ? { ok: false, message: describeMismatch(mismatch) } : { ok: true };
}
const acceptedTaskTypeSchema = z.enum(bucketAcceptedTaskTypeKey).nullable();
const acceptedChannelSchema = z.enum(taskChannel).nullable();
const acceptedDepartmentSchema = z.enum(department).nullable();
export const bucketSchema = createSelectSchema(buckets, {
    id: (schema) => schema.meta({
        description: "Bucket ID",
        example: 1,
    }),
    // Use z.coerce.date() for date fields because JSON serialization converts Date objects to ISO strings
    createdAt: () => z.coerce.date().meta({
        description: "Bucket creation timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    updatedAt: () => z.coerce.date().meta({
        description: "Bucket last update timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    createdBy: (schema) => schema.meta({
        description: "User ID who created this bucket (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    type: (schema) => schema.meta({
        description: "Bucket type key (references bucketTypes.key)",
        example: "fix_fix_none_none",
    }),
    title: (schema) => schema.meta({
        description: "Bucket title",
        example: "Sprint 1",
    }),
    description: (schema) => schema.meta({
        description: "Bucket description",
        example: "First sprint tasks",
    }),
    image: (schema) => schema.meta({
        description: "Bucket image URL",
        example: "https://example.com/image.png",
    }),
    skillLevel: (schema) => schema.meta({
        description: "Required skill level",
        example: "intermediate",
    }),
    status: (schema) => schema.meta({
        description: "Bucket status",
        example: "in_progress",
    }),
    targetQuantity: (schema) => schema.meta({
        description: "Target quantity for the bucket",
        example: 10,
    }),
    assignee: (schema) => schema.meta({
        description: "User ID assigned to this bucket (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    plannedStartTime: () => z.coerce.date().meta({
        description: "Planned start time of the bucket",
        example: "2024-01-15T08:00:00.000Z",
    }),
    plannedEndTime: () => z.coerce.date().meta({
        description: "Planned end time of the bucket",
        example: "2024-01-15T17:00:00.000Z",
    }),
    startTime: () => z.coerce.date().nullable().meta({
        description: "Actual start time of the bucket",
        example: "2024-01-15T08:15:00.000Z",
    }),
    endTime: () => z.coerce.date().nullable().meta({
        description: "Actual end time of the bucket",
        example: "2024-01-15T16:45:00.000Z",
    }),
    duration: (schema) => schema.meta({
        description: "Actual bucket duration in minutes",
        example: 480,
    }),
    acceptedTaskType: (schema) => schema.meta({
        description: "Task type this bucket pulls; set on Task Bucket titles (inbox, kostenvoranschlag, rechnung) and null on every other title",
        example: "cost_estimate",
    }),
    acceptedChannel: (schema) => schema.meta({
        description: "Channel of the tasks this bucket pulls; null means any",
        example: "email",
    }),
    acceptedDepartment: (schema) => schema.meta({
        description: "Department of the tasks this bucket pulls; null means any. Required for inbox_processing",
        example: "damage_ga",
    }),
}).describe("Bucket object");
// Base schema using callbacks - stays in sync with Drizzle column types
const baseCreateBucketSchema = createInsertSchema(buckets, {
    createdBy: () => z.uuid({ message: "Bitte gib eine gültige Ersteller-ID an" }).meta({
        description: "User ID who created this bucket (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    type: (schema) => schema.meta({
        description: "Bucket type key (references bucketTypes.key)",
        example: "fix_fix_none_none",
    }),
    title: (schema) => schema.min(1, "Bitte gib einen Titel an").meta({
        description: "Bucket title",
        example: "Sprint 1",
    }),
    description: (schema) => schema
        .meta({
        description: "Bucket description",
        example: "First sprint tasks",
    })
        .nullable(),
    image: (schema) => schema
        .meta({
        description: "Bucket image URL",
        example: "https://example.com/image.png",
    })
        .nullable(),
    skillLevel: (schema) => schema.min(1, "Bitte gib ein Skill Level an").meta({
        description: "Required skill level",
        example: "intermediate",
    }),
    status: (schema) => schema.meta({
        description: "Bucket status",
        example: "pending",
    }),
    targetQuantity: (schema) => schema
        .meta({
        description: "Target quantity for the bucket",
        example: 10,
    })
        .nullable(),
    assignee: () => z.uuid({ message: "Bitte gib eine gültige Bearbeiter-ID an" }).meta({
        description: "User ID assigned to this bucket (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    plannedStartTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .refine(hasBerlinOffset, "Offset does not match Europe/Berlin for the given instant")
        .transform((val) => rfc3339ToDate(val))
        .meta({
        description: "Planned start time (RFC 3339 with Berlin offset)",
        example: "2026-01-15T08:00:00+01:00",
    }),
    plannedEndTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .refine(hasBerlinOffset, "Offset does not match Europe/Berlin for the given instant")
        .transform((val) => rfc3339ToDate(val))
        .meta({
        description: "Planned end time (RFC 3339 with Berlin offset)",
        example: "2026-01-15T17:00:00+01:00",
    }),
    startTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual start time (RFC 3339 with offset)",
        example: "2026-01-15T08:15:00+01:00",
    }),
    endTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual end time (RFC 3339 with offset)",
        example: "2026-01-15T16:45:00+01:00",
    }),
    duration: (schema) => schema
        .meta({
        description: "Actual bucket duration in minutes",
        example: 480,
    })
        .nullable(),
    acceptedTaskType: () => acceptedTaskTypeSchema,
    acceptedChannel: () => acceptedChannelSchema,
    acceptedDepartment: () => acceptedDepartmentSchema,
});
function describeMismatch(mismatch) {
    switch (mismatch.reason) {
        case "not_a_task_bucket":
            return `${mismatch.field} is only valid for task buckets (${Object.values(taskBucketTitle).join(", ")})`;
        case "missing":
            return `acceptedTaskType is required for ${mismatch.title} buckets`;
        case "not_accepted":
            return `acceptedTaskType ${mismatch.acceptedTaskType} is not accepted by ${mismatch.title} buckets`;
        case "channel_not_allowed":
            return `acceptedChannel ${mismatch.acceptedChannel} is not allowed for task type ${mismatch.acceptedTaskType}`;
        case "department_not_allowed":
            return `acceptedDepartment ${mismatch.acceptedDepartment} is not allowed for task type ${mismatch.acceptedTaskType}`;
        case "department_missing":
            return `acceptedDepartment is required for task type ${mismatch.acceptedTaskType}`;
    }
}
/** The field a mismatch is reported on. */
function mismatchField(mismatch) {
    switch (mismatch.reason) {
        case "not_a_task_bucket":
            return mismatch.field;
        case "channel_not_allowed":
            return "acceptedChannel";
        case "department_not_allowed":
        case "department_missing":
            return "acceptedDepartment";
        case "missing":
        case "not_accepted":
            return "acceptedTaskType";
    }
}
function assertPlanningKeyValid(data, ctx) {
    const mismatch = planningKeyMismatch(data);
    if (!mismatch)
        return;
    ctx.addIssue({ code: "custom", path: [mismatchField(mismatch)], message: describeMismatch(mismatch) });
}
// Fix drizzle-zod bug: callbacks for nullable fields lose .optional() wrapper
// We extend the base schema to add .optional() to nullable fields
export const createBucketSchema = baseCreateBucketSchema
    .extend({
    description: baseCreateBucketSchema.shape.description.optional(),
    image: baseCreateBucketSchema.shape.image.optional(),
    targetQuantity: baseCreateBucketSchema.shape.targetQuantity.optional(),
    startTime: baseCreateBucketSchema.shape.startTime.optional(),
    endTime: baseCreateBucketSchema.shape.endTime.optional(),
    duration: baseCreateBucketSchema.shape.duration.optional(),
    acceptedTaskType: baseCreateBucketSchema.shape.acceptedTaskType.optional(),
    acceptedChannel: baseCreateBucketSchema.shape.acceptedChannel.optional(),
    acceptedDepartment: baseCreateBucketSchema.shape.acceptedDepartment.optional(),
})
    .refine((data) => data.plannedStartTime < data.plannedEndTime, {
    message: "Planned start time must be before planned end time",
    path: ["plannedEndTime"],
})
    .superRefine(assertPlanningKeyValid)
    .describe("Create bucket request body");
export const createBucketBatchSchema = z
    .array(createBucketSchema)
    .min(1, "At least one bucket is required.")
    .describe("Create multiple buckets request body");
const rfc3339PlannedField = z
    .string()
    .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
    .refine(hasBerlinOffset, "Offset does not match Europe/Berlin for the given instant")
    .transform((val) => rfc3339ToDate(val));
const rfc3339Field = z
    .string()
    .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
    .transform((val) => rfc3339ToDate(val));
export const updateBucketSchema = bucketSchema
    .pick({
    description: true,
    image: true,
    skillLevel: true,
    title: true,
    targetQuantity: true,
    assignee: true,
    status: true,
    type: true,
})
    .extend({
    plannedStartTime: rfc3339PlannedField.optional(),
    plannedEndTime: rfc3339PlannedField.optional(),
    startTime: rfc3339Field.nullable().optional(),
    endTime: rfc3339Field.nullable().optional(),
    acceptedTaskType: acceptedTaskTypeSchema,
    acceptedChannel: acceptedChannelSchema,
    acceptedDepartment: acceptedDepartmentSchema,
})
    .partial()
    .describe("Update Bucket request body");
// Time conflict error schema for bucket batch creation validation
export const timeConflictErrorSchema = z.object({
    userId: z.string().meta({
        description: "User ID (UUID) of the assignee",
        example: "550e8400-e29b-41d4-a716-446655440000",
    }),
    plannedStartTime: z.coerce.date().meta({
        description: "Planned start time of the conflicting bucket",
    }),
    plannedEndTime: z.coerce.date().meta({
        description: "Planned end time of the conflicting bucket",
    }),
    userName: z.string().optional().meta({
        description: "Full name of the user (firstName lastName)",
        example: "Max Mustermann",
    }),
});
// Bucket with tasks response schema
export const bucketWithTasksSchema = bucketSchema.extend({
    tasks: taskSchema.array(),
});
// Bucket with bucket type data
// Note: bucketType is guaranteed to exist via database innerJoin constraint
export const bucketWithTypeSchema = bucketSchema.extend({
    bucketType: bucketTypeSchema, // Always present due to innerJoin
});
// Bucket with bucket type data and assignee user data for Gantt chart views
// Note: bucketType is guaranteed to exist via database innerJoin constraint
export const bucketWithTypeAndAssigneeSchema = bucketSchema.extend({
    assigneeUser: userSchema,
    bucketType: bucketTypeSchema, // Always present due to innerJoin
});
